/**
 * Drawing a canvas note (#157) — the card, its wrapped text, the numbered
 * badge, and the leader line out to the part it points at.
 *
 * Built as a free function rather than a Scene-owned renderer because the same
 * layout has to be reproduced on the printed pattern, and a function taking a
 * scope can be called from the exporter's throwaway offscreen scope just as
 * easily as from the live one.
 */
import type paper from "paper";
import { cssVar } from "../colors";
import type { Note, Vec2 } from "../model/Project";
import { wrapNoteText } from "../model/notes";

const FILL = () => cssVar("--note-fill");
const BORDER = () => cssVar("--note-border");
const TEXT = () => cssVar("--note-text");
const CALLOUT = () => cssVar("--note-callout");
const BADGE_TEXT = () => cssVar("--note-badge-text");
const SELECTED = () => cssVar("--note-selected");
/** Card border weight in project mm, unselected and selected. Selection is a
 *  THICKER stroke in the canvas accent — the same language a selected part
 *  speaks — rather than a different fill, which would make a selected note
 *  look like a different kind of note. */
const BORDER_MM = 0.4;
const SELECTED_BORDER_MM = 1.5;

/**
 * Padding between the card edge and its text, in project mm — FIXED, not a
 * fraction of the type size (#1075).
 *
 * As a fraction it grew with the text: a note set large enough to read across
 * the room spent most of its card on margin, and the box the maker sized by
 * hand held almost no words. Padding is a property of the card, the way the
 * corner radius and the border weight are; only the type inside it scales.
 *
 * 2.4mm is what the ratio gave at the default 4mm type, so a note written
 * before this change looks the same after it.
 */
export const PAD_MM = 2.4;
/** Baseline-to-baseline spacing, as a fraction of font size. */
const LINE_RATIO = 1.35;
/** Room below the last baseline for descenders (g, y, p), as a fraction of
 *  font size — without it the tail of the last line sits on the card's edge. */
const DESCENDER_RATIO = 0.25;
/**
 * The numbered badge and the anchor dot are FIXED sizes in project mm, not
 * fractions of the note's type size.
 *
 * They are wayfinding marks, not part of the note's typography: the badge is
 * what ties a card on the pattern to its line on the notes page, and the dot is
 * what says which point on the part is meant. Both have to stay legible when
 * someone sets a note in small type to fit a tight margin — and scaling them up
 * with a large note would put a great blob on the drawing.
 */
export const BADGE_RADIUS_MM = 3;
export const ANCHOR_DOT_RADIUS_MM = 0.9;
const BADGE_FONT_MM = 3.6;
/**
 * The resize grip in the card's bottom-right corner — the same two-stroke
 * notch a textarea wears, for the same reason: a corner that can be dragged
 * has to look like one, or nobody finds it.
 *
 * Fixed millimetres, like the badge and the anchor dot: it is an affordance,
 * not typography, so it must not swell with the note's text size.
 */
const GRIP_MM = 2.6;
const GRIP_INSET_MM = 1;
/** Baseline drop of the badge numeral below the disc's centre, so the digits
 *  sit optically centred. Exported because a mid-drag update re-places the
 *  numeral by its BASELINE (`PointText.point`) — the same handle the renderer
 *  uses — rather than by its bounds. */
export const BADGE_LABEL_DY_MM = BADGE_FONT_MM * 0.36;
/** Card corner radius in project mm. */
const CORNER_MM = 1.2;

export interface NoteRenderOptions {
  /** Draw in flat black on white, for a monochrome print. */
  monochrome?: boolean;
  /** Draw the card and text but not the badge — used where the number is
   *  carried elsewhere. */
  hideBadge?: boolean;
  /** This is the note the panel is editing. Ignored on export: which note was
   *  selected when the file was written is not a property of the pattern. */
  selected?: boolean;
  /**
   * Draw the corner grip that says the card can be dragged bigger (#1074).
   *
   * Off by default, and the canvas is the only caller that turns it on: a grip
   * is an invitation to drag, and there is nothing to drag on a printed sheet
   * or an exported file — it would just be a mark on the pattern that means
   * nothing. An explicit flag rather than "not monochrome", so a future colour
   * export cannot pick it up by accident.
   */
  showResizeGrip?: boolean;
}

/**
 * Measure a string at `fontSizeMm` in project mm, using a throwaway PointText.
 *
 * Paper reports bounds in project units, which ARE millimeters here, so this
 * needs no DPI conversion — and it measures with the same font stack the note
 * will be drawn in, which a hand-rolled character-width estimate would not.
 */
export function paperTextMeasurer(
  scope: paper.PaperScope,
  fontSizeMm: number,
  fontFamily: string,
): (line: string) => number {
  return (line: string) => {
    if (!line) return 0;
    const probe = new scope.PointText({
      point: [0, 0],
      content: line,
      fontFamily,
      fontSize: fontSizeMm,
      insert: false,
    });
    return probe.bounds.width;
  };
}

/** The font the notes are set in. One place so the on-screen wrap and the
 *  printed wrap can't drift apart. */
export const NOTE_FONT_FAMILY = "Helvetica, Arial, sans-serif";

/**
 * How tall the card has to be to show all of `note`'s text at its current width
 * and type size (#1074).
 *
 * The wrap is the same call the renderer makes, so the two cannot disagree
 * about how many lines there are — which is the whole risk here: a height
 * derived from a different wrap than the one drawn either clips the last line
 * or leaves a band of empty card under it.
 */
export function noteContentHeightMm(
  note: Note,
  scope: paper.PaperScope,
): number {
  // The SAME padding the renderer uses (#1075) — measuring the wrap against a
  // different inset is the disagreement this function exists to prevent.
  const pad = PAD_MM;
  const measure = paperTextMeasurer(scope, note.fontSizeMm, NOTE_FONT_FAMILY);
  const lines = wrapNoteText(note.text, note.widthMm - pad * 2, measure);
  const rows = Math.max(1, lines.length);
  // Top padding, the first line's full height, each further line's leading,
  // room for descenders, then bottom padding — the same arithmetic the text
  // placement below uses, read forwards.
  return (
    pad * 2 + note.fontSizeMm * (1 + LINE_RATIO * (rows - 1) + DESCENDER_RATIO)
  );
}

/**
 * The height the card is DRAWN at: what the maker asked for, or what the text
 * needs, whichever is greater (#1074).
 *
 * The stored height is a floor rather than a cap, because a note whose words
 * are cut off is worse than useless — it is a note that lies about what it
 * says. Making the box bigger is something the maker can always see and undo;
 * silently clipping the third line is not.
 */
export function noteCardHeightMm(note: Note, scope: paper.PaperScope): number {
  return Math.max(note.heightMm, noteContentHeightMm(note, scope));
}

/**
 * Where the leader line should meet the card: the point on the card's edge
 * closest to the anchor, so the line stops at the border instead of running
 * under the text.
 *
 * `cardHeightMm` is the DRAWN height, which can exceed the stored one when the
 * text has grown the box — pass it, or the line stops short of a card that is
 * taller than its own record of itself.
 */
export function calloutLandingPoint(
  note: Note,
  anchor: Vec2,
  cardHeightMm: number = note.heightMm,
): Vec2 {
  const left = note.position.x;
  const top = note.position.y;
  const right = left + note.widthMm;
  const bottom = top + cardHeightMm;
  return {
    x: Math.min(right, Math.max(left, anchor.x)),
    y: Math.min(bottom, Math.max(top, anchor.y)),
  };
}

/**
 * Build the whole note as one group: leader line and anchor dot first (so they
 * pass under the card), then the card, its clipped text, and the badge.
 *
 * `anchors` are the already-resolved tip positions — resolving them needs the
 * project, which this function deliberately doesn't take, so the caller does
 * that once and this stays about drawing. One leader per anchor: a note can
 * label several parts at once.
 */
export function buildNoteGroup(
  note: Note,
  number: number,
  anchors: Vec2 | Vec2[] | null,
  scope: paper.PaperScope,
  opts: NoteRenderOptions = {},
): paper.Group {
  const tips: Vec2[] =
    anchors == null ? [] : Array.isArray(anchors) ? anchors : [anchors];
  const mono = opts.monochrome === true;
  // Selection is a screen state, so it never survives into a monochrome print.
  const picked = opts.selected === true && !mono;
  const ink = mono ? "#000000" : TEXT();
  const edge = mono ? "#000000" : picked ? SELECTED() : BORDER();
  const face = mono ? "#ffffff" : FILL();
  const leader = mono ? "#000000" : CALLOUT();

  const group = new scope.Group();
  group.data = { kind: "note", noteId: note.id };

  // Padding belongs to the card, not to the type (#1075).
  const pad = PAD_MM;
  // The card grows to fit its words (#1074); everything that has to line up
  // with its edges reads this, not the stored height.
  const cardH = noteCardHeightMm(note, scope);

  tips.forEach((anchor, i) => {
    const landing = calloutLandingPoint(note, anchor, cardH);
    const line = new scope.Path.Line({
      from: [anchor.x, anchor.y],
      to: [landing.x, landing.y],
      strokeColor: leader,
      strokeWidth: 0.4,
      strokeScaling: false,
    });
    // The index rides on the item so a tip dragged on the canvas re-aims the
    // callout it belongs to, and not whichever one happens to be first.
    line.data = { kind: "note-callout", noteId: note.id, anchorIndex: i };
    group.addChild(line);

    // A small dot on the part end so it is clear WHICH point is meant, and so
    // the tip stays the grab handle for re-aiming the callout.
    const dot = new scope.Path.Circle({
      center: [anchor.x, anchor.y],
      radius: ANCHOR_DOT_RADIUS_MM,
      fillColor: leader,
    });
    dot.data = { kind: "note-anchor", noteId: note.id, anchorIndex: i };
    group.addChild(dot);
  });

  const card = new scope.Path.Rectangle({
    rectangle: new scope.Rectangle(
      new scope.Point(note.position.x, note.position.y),
      new scope.Size(note.widthMm, cardH),
    ),
    radius: CORNER_MM,
    fillColor: face,
    strokeColor: edge,
    strokeWidth: picked ? SELECTED_BORDER_MM : BORDER_MM,
    strokeScaling: false,
  });
  card.data = { kind: "note-card", noteId: note.id, selected: picked };
  group.addChild(card);

  // Text is clipped to the card: the box height is whatever the maker dragged,
  // independent of how much they typed, so overflow has to be cut rather than
  // spilling across the pattern.
  const measure = paperTextMeasurer(scope, note.fontSizeMm, NOTE_FONT_FAMILY);
  const lines = wrapNoteText(note.text, note.widthMm - pad * 2, measure);
  const textGroup = new scope.Group();
  const clip = new scope.Path.Rectangle({
    rectangle: new scope.Rectangle(
      new scope.Point(note.position.x, note.position.y),
      new scope.Size(note.widthMm, cardH),
    ),
    radius: CORNER_MM,
  });
  clip.clipMask = true;
  textGroup.addChild(clip);
  lines.forEach((line, i) => {
    if (!line) return;
    const item = new scope.PointText({
      point: [
        note.position.x + pad,
        note.position.y + pad + note.fontSizeMm * (1 + LINE_RATIO * i),
      ],
      content: line,
      fillColor: ink,
      fontFamily: NOTE_FONT_FAMILY,
      fontSize: note.fontSizeMm,
    });
    // Tagged so the body can be told apart from the badge numeral, which is
    // deliberately a fixed size — without the tag a test measuring "the text"
    // measures whichever happens to be taller.
    item.data = { kind: "note-text", noteId: note.id };
    textGroup.addChild(item);
  });
  group.addChild(textGroup);

  if (opts.showResizeGrip === true) {
    // Two strokes parallel to the corner's diagonal, shorter one outside —
    // read as a grip rather than as a broken border.
    const right = note.position.x + note.widthMm - GRIP_INSET_MM;
    const bottom = note.position.y + cardH - GRIP_INSET_MM;
    const grip = new scope.Group();
    grip.data = { kind: "note-resize-grip", noteId: note.id };
    for (const len of [GRIP_MM, GRIP_MM * 0.55]) {
      const line = new scope.Path.Line({
        from: [right - len, bottom],
        to: [right, bottom - len],
        strokeColor: edge,
        strokeWidth: 0.5,
        strokeScaling: false,
      });
      grip.addChild(line);
    }
    group.addChild(grip);
  }

  if (opts.hideBadge !== true) {
    const r = BADGE_RADIUS_MM;
    // The numbered badge sits where the leader MEETS THE CARD, not on the card
    // corner and not on the part: it reads as the head of the note it labels,
    // and the card edge is where the eye arrives coming down the line.
    //
    // A note pointing at nothing has no leader, so its badge falls back to the
    // card's corner. With several leaders it sits at the first one's card end —
    // one head for the note, not one per line.
    const landing = tips[0] ? calloutLandingPoint(note, tips[0], cardH) : null;
    const centre = landing
      ? new scope.Point(landing.x, landing.y)
      : new scope.Point(note.position.x, note.position.y);
    const badge = new scope.Path.Circle({
      center: centre,
      radius: r,
      fillColor: leader,
    });
    // Anchored, the badge IS the grab handle for pulling out a new callout.
    // It rides the FIRST callout's card end, so it carries that index: a part
    // drag has to move the badge with the line it sits on.
    badge.data = { kind: "note-badge", noteId: note.id, anchorIndex: 0 };
    group.addChild(badge);
    const label = new scope.PointText({
      point: [centre.x, centre.y + BADGE_LABEL_DY_MM],
      content: String(number),
      fillColor: mono ? "#ffffff" : BADGE_TEXT(),
      fontFamily: NOTE_FONT_FAMILY,
      fontSize: BADGE_FONT_MM,
      justification: "center",
    });
    // Tagged so the number travels with the disc it sits in. Untagged, a
    // mid-drag update moved the badge and left the numeral behind.
    label.data = { kind: "note-badge-label", noteId: note.id, anchorIndex: 0 };
    group.addChild(label);
  }

  return group;
}
